import React from 'react';
import { products } from '../data/products';

const flows = {
  ride: ['Authorize', 'Estimated fare', 'Create booking'],
  delivery: ['Authorize', 'Get fare', 'Create order'],
  fleet: ['Quickstart', 'Integrate', 'Go live'],
};

const steps = [
  { num: '01', title: 'Choose a product', desc: 'Open WiRide or WiDeliver here, or jump to WiFleet docs. Each API has its own pages and payloads.' },
  { num: '02', title: 'Authorize', desc: 'Send your bearerkey, save the JWT, and optionally register an HTTPS webhook for live status updates.' },
  { num: '03', title: 'Call the APIs', desc: 'Estimate, create, list, edit, and cancel from the docs. Try It runs against Sandbox or Live from the header.' },
];

const audiences = [
  {
    productId: 'ride',
    title: 'Corporate teams',
    desc: 'HR, travel, and ops apps that need to book, quote, and cancel staff rides.',
    product: 'WiRide',
  },
  {
    productId: 'delivery',
    title: 'Restaurants',
    desc: 'Ordering platforms that dispatch food from a branch to a customer address.',
    product: 'WiDeliver',
  },
  {
    productId: 'fleet',
    title: 'Fleet operators',
    desc: 'Teams that run vehicles and trips. Docs are hosted separately.',
    product: 'WiFleet',
  },
];

const useCases = [
  { productId: 'ride', product: 'WiRide', title: 'Staff airport transfer', desc: 'Book a corporate ride to or from the airport.' },
  { productId: 'ride', product: 'WiRide', title: 'Quote before booking', desc: 'Show an estimated fare in your app, then create the trip.' },
  { productId: 'delivery', product: 'WiDeliver', title: 'Restaurant dispatch', desc: 'Send an order from a branch to a drop-off address.' },
  { productId: 'delivery', product: 'WiDeliver', title: 'Multi-branch ops', desc: 'List branches and assign the right pickup location.' },
  { productId: 'fleet', product: 'WiFleet', title: 'Vehicle operations', desc: 'Run fleet workflows from the hosted WiFleet docs.' },
  { productId: 'fleet', product: 'WiFleet', title: 'Trip tracking', desc: 'Follow live trip status in your fleet product.' },
];

const compareRows = [
  { productId: 'ride', name: 'WiRide', bestFor: 'People & corporate travel', job: 'Book, quote, list, edit, cancel rides' },
  { productId: 'delivery', name: 'WiDeliver', bestFor: 'Food & grocery dispatch', job: 'Quote, dispatch, branches, wallet' },
  { productId: 'fleet', name: 'WiFleet', bestFor: 'Vehicles & fleet ops', job: 'Fleet APIs on hosted docs' },
];

const stats = [
  { label: 'Protocol', value: 'REST · JSON' },
  { label: 'Auth', value: 'bearerkey + JWT' },
  { label: 'Events', value: 'Signed webhooks' },
  { label: 'Environments', value: 'Sandbox & Live' },
];

function ThemeToggle({ theme, toggleTheme }) {
  return (
    <button
      type="button"
      className="app-header__theme app-header__icon-btn"
      onClick={toggleTheme}
      title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
      aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
    >
      {theme === 'dark' ? (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="5" />
          <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
          <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
        </svg>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      )}
    </button>
  );
}

export default function LandingPage({ theme, toggleTheme, onOpenProduct }) {
  const scrollToProducts = () => {
    document.getElementById('products')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="landing">
      <header className="landing__nav">
        <div className="landing__nav-inner">
          <div className="landing__brand">
            <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Wiyak" className="landing__logo" />
            <div>
              <div className="landing__brand-name">Wiyak Developers</div>
              <div className="landing__brand-sub">API documentation</div>
            </div>
          </div>
          <nav className="landing__links" aria-label="Landing">
            <a href="#products" onClick={(e) => { e.preventDefault(); scrollToProducts(); }}>Products</a>
            <a href="#audience" onClick={(e) => { e.preventDefault(); document.getElementById('audience')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>Who it's for</a>
            <a href="#use-cases" onClick={(e) => { e.preventDefault(); document.getElementById('use-cases')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>Use cases</a>
            <a href="#compare" onClick={(e) => { e.preventDefault(); document.getElementById('compare')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>Compare</a>
            <a href="mailto:support@wiyak.com">Support</a>
          </nav>
          <div className="landing__nav-actions">
            <ThemeToggle theme={theme} toggleTheme={toggleTheme} />
            <button type="button" className="landing__nav-cta" onClick={scrollToProducts}>
              View API Docs
            </button>
          </div>
        </div>
      </header>

      <section className="landing__hero">
        <div className="landing__hero-glow" aria-hidden="true" />
        <div className="landing__hero-inner">
          <div className="landing__hero-copy">
            <div className="landing__pill">
              <span className="landing__pill-dot" />
              Wiyak Developer Platform
            </div>
            <h1 className="landing__title">
              Build on Wiyak
              <span>classic APIs, modern docs</span>
            </h1>
            <p className="landing__lead">
              REST documentation for corporate rides, restaurant delivery, and fleet operations. Separate pages, real request and response examples, Try It against Sandbox or Live, and signed webhooks.
            </p>
            <div className="landing__hero-actions">
              <button type="button" className="landing__btn landing__btn--primary" onClick={() => onOpenProduct('ride', 'home')}>
                Explore WiRide
              </button>
              <button type="button" className="landing__btn landing__btn--ghost" onClick={() => onOpenProduct('delivery', 'home')}>
                Explore WiDeliver
              </button>
              <a
                className="landing__btn landing__btn--ghost"
                href="https://docs.wiyak.online/#quickstart"
                target="_blank"
                rel="noopener noreferrer"
              >
                Explore WiFleet
              </a>
            </div>
          </div>

          <aside className="landing__terminal" aria-hidden="true">
            <div className="landing__terminal-bar">
              <span /><span /><span />
              <em>authorize.sh</em>
            </div>
            <pre>{`POST /authorize
Authorization: bearerkey

{
  "web_hook_url": "https://you.com/hook"
}

← 200 OK
{
  "status": true,
  "token": "eyJhbGciOi…"
}`}</pre>
          </aside>
        </div>
      </section>

      <section className="landing__stats" aria-label="Platform facts">
        {stats.map(s => (
          <div key={s.label} className="landing__stat">
            <span>{s.label}</span>
            <strong>{s.value}</strong>
          </div>
        ))}
      </section>

      <section className="landing__products" id="products">
        <div className="landing__section-head">
          <h2>Choose your API</h2>
          <p>Same documentation UI. Different endpoints, bodies, and environments for each product.</p>
        </div>
        <div className="landing__grid">
          {products.map(product => {
            const apis = product.apis || [];
            const envs = (product.environments || []).filter(e => e.id !== 'custom');
            return (
            <article key={product.id} className="landing-card" data-product={product.id} style={{ '--card-accent': product.color }}>
              <div className="landing-card__accent" />
              <div className="landing-card__top">
                {product.logoWide ? (
                  <img
                    src={`${process.env.PUBLIC_URL}${product.logo}`}
                    alt={product.logoAlt || product.name}
                    className="landing-card__logo"
                  />
                ) : (
                  <div className="landing-card__ride-brand">
                    <img src={`${process.env.PUBLIC_URL}${product.logo}`} alt={product.logoAlt || product.name} />
                    <div>
                      <h2 className="landing-card__name">{product.name}</h2>
                      <p className="landing-card__tag">{product.tagline}</p>
                    </div>
                  </div>
                )}
                {product.logoWide && <p className="landing-card__tag">{product.tagline}</p>}
              </div>
              <p className="landing-card__desc">{product.description}</p>
              <div className="landing-card__flow">
                {(flows[product.id] || []).map((step, i) => (
                  <React.Fragment key={step}>
                    {i > 0 && <span className="landing-card__flow-sep">→</span>}
                    <span>{step}</span>
                  </React.Fragment>
                ))}
              </div>
              <div className="landing-card__meta">
                {product.externalUrl ? (
                  <span>Hosted docs · docs.wiyak.online</span>
                ) : (
                  <>
                    <span>{apis.length} endpoints</span>
                    <span>{envs.map(e => e.label).join(' · ')}</span>
                  </>
                )}
              </div>
              {apis.length > 0 && (
              <ul className="landing-card__apis">
                {apis.slice(0, 6).map(api => (
                  <li key={api.id}>
                    <button type="button" onClick={() => onOpenProduct(product.id, api.id)}>
                      <span className="landing-card__method">{api.method || 'POST'}</span>
                      <span className="landing-card__api-name">{api.name}</span>
                    </button>
                  </li>
                ))}
                {apis.length > 6 && (
                  <li className="landing-card__more">+{apis.length - 6} more in the docs</li>
                )}
              </ul>
              )}
              {product.externalUrl && product.previewLinks?.length > 0 && (
              <ul className="landing-card__apis">
                {product.previewLinks.map(item => (
                  <li key={item.name}>
                    <a
                      href={product.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={item.hint}
                    >
                      <span className="landing-card__method landing-card__method--docs">DOCS</span>
                      <span className="landing-card__api-name">{item.name}</span>
                    </a>
                  </li>
                ))}
                <li className="landing-card__more">Opens docs.wiyak.online</li>
              </ul>
              )}
              {product.externalUrl ? (
                <a
                  className="landing-card__cta"
                  href={product.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open {product.headerLabel}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                    <path d="M15 3h6v6" />
                    <path d="M10 14L21 3" />
                  </svg>
                </a>
              ) : (
                <button
                  type="button"
                  className="landing-card__cta"
                  onClick={() => onOpenProduct(product.id, 'home')}
                >
                  Open {product.headerLabel}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <path d="M5 12h14M13 6l6 6-6 6" />
                  </svg>
                </button>
              )}
            </article>
            );
          })}
        </div>
      </section>

      <section className="landing__band" id="audience">
        <div className="landing__section-head">
          <h2>Who it's for</h2>
          <p>Pick the product that matches the team you are building for. Open the docs when you are ready to integrate.</p>
        </div>
        <div className="landing__audiences">
          {audiences.map(item => (
            <article key={item.productId} className="landing__tile">
              <p className="landing__tile-kicker">{item.product}</p>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
              <button type="button" onClick={() => onOpenProduct(item.productId, 'home')}>
                Open {item.product}
              </button>
            </article>
          ))}
        </div>
      </section>

      <section className="landing__band" id="use-cases">
        <div className="landing__section-head">
          <h2>What you can build</h2>
          <p>Common jobs for each API. Details, payloads, and Try It live in the product docs — not here.</p>
        </div>
        <div className="landing__usecases">
          {useCases.map(item => (
            <button
              key={item.title}
              type="button"
              className="landing__tile landing__tile--btn"
              onClick={() => onOpenProduct(item.productId, 'home')}
            >
              <p className="landing__tile-kicker">{item.product}</p>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </button>
          ))}
        </div>
      </section>

      <section className="landing__band" id="compare">
        <div className="landing__section-head">
          <h2>Compare products</h2>
          <p>Same Wiyak platform. Different jobs. Choose one row and open that documentation.</p>
        </div>
        <div className="landing__table-wrap">
          <table className="landing__table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Best for</th>
                <th>Typical job</th>
              </tr>
            </thead>
            <tbody>
              {compareRows.map(row => (
                <tr key={row.productId}>
                  <td>
                    <button type="button" className="landing__table-link" onClick={() => onOpenProduct(row.productId, 'home')}>
                      {row.name}
                    </button>
                  </td>
                  <td>{row.bestFor}</td>
                  <td>{row.job}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="landing__how">
        <div className="landing__section-head">
          <h2>How it works</h2>
          <p>Three steps from this page to a live integration.</p>
        </div>
        <div className="landing__steps">
          {steps.map(s => (
            <article key={s.num} className="landing__step">
              <span>{s.num}</span>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="landing__support" aria-labelledby="landing-support-title">
        <div className="landing__support-inner">
          <img
            src={`${process.env.PUBLIC_URL}/developer_img-1.png`}
            alt=""
            className="landing__support-art"
          />
          <div className="landing__support-copy">
            <p className="landing__support-kicker">Engineering Support</p>
            <h2 id="landing-support-title">Have more questions?</h2>
            <p>
              Having trouble building an integration or need more one on one guidance?
              Submit a ticket and one of our experienced support engineers will be happy to assist you.
            </p>
            <a className="landing__support-cta" href="mailto:support@wiyak.com">
              Contact a support engineer
              <span aria-hidden="true">»</span>
            </a>
          </div>
        </div>
      </section>

      <footer className="landing__footer">
        <div className="landing__footer-inner">
          <div className="landing__brand">
            <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Wiyak" className="landing__logo landing__logo--sm" />
            <span>Wiyak API Docs v1.0</span>
          </div>
          <a href="mailto:support@wiyak.com">support@wiyak.com</a>
        </div>
      </footer>
    </div>
  );
}
